
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";

type FeedbackType = 'general' | 'bug_report' | 'feature_request' | 'airport_request';

interface FeedbackData {
  type: FeedbackType;
  content: string;
  airport_iata: string;
  airport_name: string;
  airport_location: string;
}

interface FeedbackTabProps {
  isMobile: boolean;
}

export function FeedbackTab({ isMobile }: FeedbackTabProps) {
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const { t } = useTranslation();
  const [feedbackData, setFeedbackData] = useState<FeedbackData>({
    type: 'general',
    content: '',
    airport_iata: '',
    airport_name: '',
    airport_location: ''
  });

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackData.content) {
      toast.error("Please provide feedback content");
      return;
    }
    
    setIsSubmittingFeedback(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be logged in to submit feedback');

      const { error } = await supabase.from('feedback').insert({
        type: feedbackData.type,
        content: feedbackData.content,
        airport_iata: feedbackData.airport_iata || null,
        airport_name: feedbackData.airport_name || null,
        airport_location: feedbackData.airport_location || null,
        user_id: user.id
      });

      if (error) throw error;

      toast.success('Feedback submitted successfully');
      setFeedbackData({
        type: 'general',
        content: '',
        airport_iata: '',
        airport_name: '',
        airport_location: ''
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('submit_feedback')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleFeedbackSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="feedback-type">{t('feedback_type')}</Label>
            <select
              id="feedback-type"
              className="w-full p-2 border rounded-md bg-background"
              value={feedbackData.type}
              onChange={(e) => setFeedbackData(prev => ({
                ...prev,
                type: e.target.value as FeedbackType
              }))}
            >
              <option value="general">{t('general_feedback')}</option>
              <option value="bug_report">{t('bug_report')}</option>
              <option value="feature_request">{t('feature_request')}</option>
              <option value="airport_request">{t('airport_request')}</option>
            </select>
          </div>

          {feedbackData.type === 'airport_request' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="airport-iata">{t('airport_iata')}</Label>
                <Input
                  id="airport-iata"
                  placeholder="e.g., LAX"
                  maxLength={3}
                  value={feedbackData.airport_iata}
                  onChange={(e) => setFeedbackData(prev => ({
                    ...prev,
                    airport_iata: e.target.value.toUpperCase()
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="airport-name">{t('airport_name')}</Label>
                <div className="relative">
                  <Input
                    id="airport-name"
                    placeholder="e.g., Los Angeles International Airport"
                    maxLength={50}
                    value={feedbackData.airport_name}
                    onChange={(e) => setFeedbackData(prev => ({
                      ...prev,
                      airport_name: e.target.value
                    }))}
                  />
                  <span className="absolute right-2 top-2 text-sm text-muted-foreground">
                    {feedbackData.airport_name.length}/50
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="airport-location">{t('airport_location')}</Label>
                <div className="relative">
                  <Input
                    id="airport-location"
                    placeholder="e.g., Los Angeles, CA, USA"
                    maxLength={50}
                    value={feedbackData.airport_location}
                    onChange={(e) => setFeedbackData(prev => ({
                      ...prev,
                      airport_location: e.target.value
                    }))}
                  />
                  <span className="absolute right-2 top-2 text-sm text-muted-foreground">
                    {feedbackData.airport_location.length}/50
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="feedback-content">{t('your_feedback')}</Label>
              <span className="text-sm text-muted-foreground">
                {feedbackData.content.length}/200
              </span>
            </div>
            <textarea
              id="feedback-content"
              className="w-full min-h-[100px] p-2 border rounded-md bg-background"
              placeholder="Please provide your feedback here..."
              value={feedbackData.content}
              maxLength={200}
              onChange={(e) => setFeedbackData(prev => ({
                ...prev,
                content: e.target.value
              }))}
              required
            />
          </div>

          <Button type="submit" disabled={isSubmittingFeedback} className={isMobile ? 'w-full' : ''}>
            {isSubmittingFeedback && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('submit_feedback')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
